import { AfterViewInit, Directive, ElementRef, inject, NgZone, OnDestroy, output } from '@angular/core';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { debounceTime, fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const addressNameFormat = {
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    administrative_area_level_1: 'short_name',
    country: 'long_name',
    postal_code: 'short_name',
    postal_town: 'long_name',
};
export function getAddressComponent(type: string, addressComponents: google.maps.places.AddressComponent[]): string {
    for (const component of addressComponents) {
        if (component.types[0] === type) {
            return addressNameFormat[type] === 'short_name' ? (component.shortText ?? '') : (component.longText ?? '');
        }
    }

    return '';
}

export function getCityComponent(place: google.maps.places.Place): string {
    const components = place.addressComponents ?? [];
    const baseCity = getAddressComponent('locality', components);
    return baseCity ? baseCity : getAddressComponent('postal_town', components);
}
setOptions({
    key: 'AIzaSyC7K-K4VZtf5JIogYgbmvptct1IyI63wf0',
    v: 'weekly',
    libraries: ['places'],
    language: 'en',
});

@Directive({
    selector: '[appGoogleMapsAutocomplete]',
    standalone: true,
})
export class GoogleMapsAutocompleteDirective implements AfterViewInit, OnDestroy {
    el = inject(ElementRef<any>);
    ngZone = inject(NgZone);
    placeChangeEvent = output<google.maps.places.Place>();

    private inputElement: HTMLInputElement;
    private dropdownElement: HTMLElement;
    private sessionToken: google.maps.places.AutocompleteSessionToken;
    private destroy$ = new Subject<void>();
    private suggestions: google.maps.places.AutocompleteSuggestion[] = [];
    private highlightedIndex = -1;
    private skipNextFetch = false;
    private destroyed = false;
    private documentClickHandler: (e: MouseEvent) => void;

    ngAfterViewInit(): void {
        this.inputElement = this.el.nativeElement.querySelector('input');
        if (!this.inputElement) throw new Error('Input element not found!');

        importLibrary('places').then(() => {
            if (this.destroyed) return;

            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
            this.createDropdown();
            this.setupInputListener();
            this.setupKeyboardNavigation();

            this.documentClickHandler = (e: MouseEvent) => {
                if (!this.dropdownElement.contains(e.target as Node) && e.target !== this.inputElement) {
                    this.hideDropdown();
                }
            };
            document.addEventListener('click', this.documentClickHandler);
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        if (this.dropdownElement) {
            this.dropdownElement.innerHTML = '';
            this.dropdownElement.remove();
        }
        if (this.documentClickHandler) {
            document.removeEventListener('click', this.documentClickHandler);
            this.documentClickHandler = null;
        }
    }

    private createDropdown(): void {
        this.dropdownElement = document.createElement('div');
        this.dropdownElement.classList.add('gmp-autocomplete-dropdown');

        const wrapper = this.inputElement.closest('mat-form-field') || this.inputElement.parentElement;
        if (wrapper) {
            (wrapper as HTMLElement).style.position = 'relative';
            wrapper.appendChild(this.dropdownElement);
        }
    }

    private setupInputListener(): void {
        fromEvent(this.inputElement, 'input')
            .pipe(debounceTime(300), takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.skipNextFetch) {
                    this.skipNextFetch = false;
                    return;
                }
                const value = this.inputElement.value;
                if (value.length < 3) {
                    this.hideDropdown();
                    return;
                }
                this.fetchSuggestions(value);
            });
    }

    private setupKeyboardNavigation(): void {
        fromEvent<KeyboardEvent>(this.inputElement, 'keydown')
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (this.dropdownElement.style.display === 'none') return;

                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.suggestions.length - 1);
                        this.updateHighlight();
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                        this.updateHighlight();
                        break;
                    case 'Enter':
                        event.preventDefault();
                        if (this.highlightedIndex >= 0) {
                            this.selectSuggestion(this.suggestions[this.highlightedIndex]);
                        }
                        break;
                    case 'Escape':
                        this.hideDropdown();
                        break;
                }
            });
    }

    private async fetchSuggestions(inputValue: string): Promise<void> {
        try {
            const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: inputValue,
                sessionToken: this.sessionToken,
                includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
                language: 'en',
            });

            if (this.destroyed) return;

            this.suggestions = suggestions;
            this.highlightedIndex = -1;
            this.renderSuggestions();
        } catch {
            this.hideDropdown();
        }
    }

    private renderSuggestions(): void {
        if (this.suggestions.length === 0) {
            this.hideDropdown();
            return;
        }

        this.dropdownElement.innerHTML = '';
        this.suggestions.forEach((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            if (!prediction) return;

            const item = document.createElement('div');
            item.classList.add('gmp-autocomplete-dropdown__item');
            item.textContent = prediction.text.toString();
            item.addEventListener('mouseenter', () => {
                this.highlightedIndex = index;
                this.updateHighlight();
            });
            item.addEventListener('click', () => this.selectSuggestion(suggestion));
            this.dropdownElement.appendChild(item);
        });

        this.dropdownElement.style.display = 'block';
    }

    private updateHighlight(): void {
        const items = this.dropdownElement.children;
        for (let i = 0; i < items.length; i++) {
            items[i].classList.toggle('gmp-autocomplete-dropdown__item--highlighted', i === this.highlightedIndex);
        }
    }

    private async selectSuggestion(suggestion: google.maps.places.AutocompleteSuggestion): Promise<void> {
        const prediction = suggestion.placePrediction;
        if (!prediction) return;

        this.hideDropdown();

        const place = prediction.toPlace();
        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] });

        if (this.destroyed) return;

        // Refresh session token after selection
        this.sessionToken = new google.maps.places.AutocompleteSessionToken();

        this.ngZone.run(() => {
            if (!place.location) return;

            this.skipNextFetch = true;
            this.inputElement.value = place.formattedAddress ?? '';
            this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));

            this.placeChangeEvent.emit(place);
        });
    }

    private hideDropdown(): void {
        this.dropdownElement.style.display = 'none';
        this.suggestions = [];
        this.highlightedIndex = -1;
    }
}
